import { createDinero } from '@mr-yum/frontend-core/dist/services/createDinero'
import {
  CenterSpinner,
  ChevronUpIcon,
  ConfirmationModal,
  ErrorNotification,
  GiftOutlineIcon,
} from '@mr-yum/frontend-ui'
import cn from 'classnames'
import React, { useCallback, useContext, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { UseMutationExecute } from 'urql'

import { CartSubTotal } from '@/components/Cart/CartSubTotal'
import PubPlus from '@/components/Icons/PubPlus'
import {
  useOrderingTypeContext,
  useVenueContext,
} from '@/contexts/VenueOrderContext'
import {
  CartDiscount,
  CartDiscountRemoveByCode,
  CartPageQuery,
  CartServiceChargeRemove,
  Exact,
  RemoveCartDiscountByCodeMutation,
  RemoveCartServiceChargeMutation,
} from '@/gql/graphql'

import { CheckoutContext } from './CheckoutCTA'

const EXTERNAL_LOYALTY_CODE = 'ExternalLoyalty'

interface Props {
  cart: CartPageQuery['getCart']
  allowSurcountRemoval?: boolean
  allowServiceChargeRemoval?: boolean
  removeDiscount: UseMutationExecute<
    RemoveCartDiscountByCodeMutation,
    Exact<{
      input: CartDiscountRemoveByCode
    }>
  >
  removeServiceCharge: UseMutationExecute<
    RemoveCartServiceChargeMutation,
    Exact<{
      input: CartServiceChargeRemove
    }>
  >
}

const shouldDisableServiceChargeButton = (
  isRemovable: boolean,
  allowServiceChargeRemoval: boolean,
) => !isRemovable || (isRemovable && !allowServiceChargeRemoval)

const LoyaltyRewardSummary: React.FC<{
  externalLoyaltyDiscountsSummary:
    | {
        name: string
        displayInCartSummary: boolean | null | undefined
      }[]
    | undefined
}> = ({ externalLoyaltyDiscountsSummary }) => (
  <details className="group" open={true}>
    <summary className="flex list-none [&::-webkit-details-marker]:hidden">
      <div className="flex items-center gap-0.5">
        Rewards
        <ChevronUpIcon className="transform transition-transform duration-200 group-open:rotate-180" />
      </div>
    </summary>
    {externalLoyaltyDiscountsSummary
      ?.filter((discount) => discount.displayInCartSummary)
      ?.map((discount, index) => (
        <div
          key={index}
          className="mt-1 flex justify-start text-sm text-semantic-success-foreground-bold"
        >
          <GiftOutlineIcon className="h-4 w-4 self-center" />
          <span className="ml-1">{discount.name}</span>
        </div>
      ))}
  </details>
)

export const CartSummary = ({
  cart,
  allowSurcountRemoval = false,
  allowServiceChargeRemoval = true,
  removeDiscount,
  removeServiceCharge,
}: Props) => {
  const { loading, serviceChargeError } = useContext(CheckoutContext)
  const intl = useIntl()
  const { orderingType } = useOrderingTypeContext()
  const { venueSlug } = useVenueContext()
  const [surcount, setSurcount] = useState<CartDiscount | undefined>(undefined)
  const [showRemoveServiceCharge, setShowRemoveServiceCharge] = useState(false)

  const toggleServiceChargeModal = useCallback(
    (open: boolean) => () => setShowRemoveServiceCharge(open),
    [setShowRemoveServiceCharge],
  )

  const externalLoyaltyDiscountsSummary = cart?.loyaltyDiscounts?.map(
    (discount) => {
      return {
        name: discount.name,
        displayInCartSummary: discount.displayInCartSummary,
      }
    },
  )

  const hasPubPlusDiscount = externalLoyaltyDiscountsSummary?.some((discount) =>
    discount.name.toLowerCase().includes('pub+'),
  )

  const removeSurcountIfRemovable = useCallback(async () => {
    if (allowSurcountRemoval && surcount) {
      const { discountInCents, isRemovable, code } = surcount
      if (discountInCents > 0 && isRemovable) {
        setSurcount(undefined)
        await removeDiscount({
          input: {
            code: code || '',
            venueSlug,
            orderingType,
          },
        })
      }
    }
  }, [
    venueSlug,
    orderingType,
    allowSurcountRemoval,
    removeDiscount,
    surcount,
    setSurcount,
  ])

  const removeServiceChargeIfRemovable = useCallback(async () => {
    if (allowSurcountRemoval && !!cart?.total?.cartServiceCharge?.isRemovable) {
      toggleServiceChargeModal(false)()
      await removeServiceCharge({
        input: {
          venueSlug,
          orderingType,
        },
      })
    }
  }, [
    venueSlug,
    orderingType,
    removeServiceCharge,
    allowSurcountRemoval,
    cart?.total?.cartServiceCharge?.isRemovable,
    toggleServiceChargeModal,
  ])

  if (loading) return <CenterSpinner />

  return (
    <>
      {cart && (
        <>
          {serviceChargeError && (
            <ErrorNotification fullWidth error={serviceChargeError} />
          )}

          <div className="space-y-6">
            <div className="space-y-1">
              <CartSubTotal
                subTotalInCents={cart.total.subTotalInCents}
                currency={cart.currency}
              />

              {cart?.total.cartDiscounts
                ?.sort(
                  (a, b) =>
                    (a.code === EXTERNAL_LOYALTY_CODE ? 1 : 0) -
                    (b.code === EXTERNAL_LOYALTY_CODE ? 1 : 0),
                )
                .map((discount, index) => {
                  const isExternalLoyalty =
                    discount.code === EXTERNAL_LOYALTY_CODE
                  const amount =
                    (discount.discountRate < 0 || isExternalLoyalty
                      ? '-'
                      : '') +
                    createDinero(
                      Math.abs(discount.discountInCents),
                      cart.currency,
                    ).toFormat()
                  const surcountRemoveable =
                    allowSurcountRemoval &&
                    discount &&
                    discount.discountInCents > 0 &&
                    discount.isRemovable

                  return (
                    <div
                      key={index}
                      className={cn(
                        'mb-[2px] flex items-baseline justify-between sm:mb-1',
                        {
                          'text-semantic-success-foreground-bold':
                            discount.discountRate < 0 || isExternalLoyalty,
                        },
                      )}
                    >
                      <button
                        disabled={!surcountRemoveable}
                        data-testid="surcount-button"
                        className="text-left my-body-md"
                        type="button"
                        onClick={() => setSurcount(discount)}
                      >
                        {discount.discountName === 'Loyalty Discount' &&
                        isExternalLoyalty ? (
                          <LoyaltyRewardSummary
                            externalLoyaltyDiscountsSummary={
                              externalLoyaltyDiscountsSummary
                            }
                          />
                        ) : (
                          <div className="flex items-center gap-2 ">
                            {discount.discountName}
                            {hasPubPlusDiscount && isExternalLoyalty && (
                              <PubPlus width="24" height="24" />
                            )}
                          </div>
                        )}
                      </button>
                      <div className="my-body-md">{amount}</div>
                    </div>
                  )
                })}

              {cart?.total?.cartServiceCharge && (
                <div className="flex items-baseline justify-between">
                  <button
                    disabled={shouldDisableServiceChargeButton(
                      cart.total.cartServiceCharge.isRemovable,
                      allowServiceChargeRemoval,
                    )}
                    className="my-body-md"
                    type="button"
                    onClick={toggleServiceChargeModal(true)}
                  >
                    {cart.total.cartServiceCharge.chargeName}
                  </button>
                  <div className="my-body-md">
                    {createDinero(
                      Math.abs(cart.total.cartServiceCharge.chargeInCents),
                      cart.currency,
                    ).toFormat()}
                  </div>
                </div>
              )}

              {cart.total.gratuityInCents > 0 && (
                <div className="flex items-baseline justify-between">
                  <div className="my-body-md">
                    <FormattedMessage
                      defaultMessage="Tip"
                      id="mlXW3J"
                      description="Total monetary tip amount for the venue"
                    />
                  </div>
                  <div className="my-body-md">
                    {createDinero(
                      cart.total.gratuityInCents,
                      cart.currency,
                    ).toFormat()}
                  </div>
                </div>
              )}

              {cart.total.deliveryFeeInCents > 0 && (
                <div className="flex items-baseline justify-between">
                  <div className="my-body-md">
                    <FormattedMessage
                      defaultMessage="Delivery fee"
                      id="jCB9UK"
                    />
                  </div>
                  <div className="my-body-md">
                    {createDinero(
                      cart.total.deliveryFeeInCents!,
                      cart.currency,
                    ).toFormat()}
                  </div>
                </div>
              )}

              {cart.total.cardSurchargeInCents > 0 && (
                <div className="flex items-baseline justify-between">
                  <div className="my-body-md">
                    {cart.total.cardSurchargeName ? (
                      cart.total.cardSurchargeName
                    ) : (
                      <FormattedMessage
                        defaultMessage="Card surcharge"
                        id="N1Cjf1"
                        description="Card surcharge applied to the cart value"
                      />
                    )}
                  </div>
                  <div className="my-body-md">
                    {createDinero(
                      cart.total.cardSurchargeInCents!,
                      cart.currency,
                    ).toFormat()}
                  </div>
                </div>
              )}

              {cart.total.totalTaxInCents > 0 && !cart.total.isTaxInclusive && (
                <div className="flex items-baseline justify-between">
                  <div className="my-body-md">{cart.total.taxName}</div>
                  <div className="my-body-md">
                    {createDinero(
                      cart.total.totalTaxInCents!,
                      cart.currency,
                    ).toFormat()}
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-baseline justify-between">
              <div className="my-heading-sm">
                <FormattedMessage
                  defaultMessage="Total"
                  id="gF1t+8"
                  description="Total amount of the cart"
                />
              </div>
              <div data-testid="total" className="my-heading-sm">
                {cart?.currency &&
                  createDinero(
                    cart?.total.totalInCents || 0,
                    cart?.currency,
                  ).toFormat()}
              </div>
            </div>
          </div>

          {allowSurcountRemoval && (
            <ConfirmationModal
              isOpen={!!surcount}
              onClose={() => setSurcount(undefined)}
              onConfirm={() => removeSurcountIfRemovable()}
              title={
                surcount &&
                intl.formatMessage(
                  {
                    defaultMessage: 'Remove {surcountName}?',
                    id: 'XhsDq5',
                  },
                  { surcountName: surcount.discountName },
                )
              }
              description={intl.formatMessage({
                defaultMessage: 'Are you sure you want to remove this?',
                id: 'beq9m5',
              })}
              cancelLabel={intl.formatMessage({
                defaultMessage: 'Keep',
                id: 'pNTyiX',
              })}
              confirmLabel={intl.formatMessage({
                defaultMessage: 'Remove',
                id: 'G/yZLu',
              })}
            />
          )}

          {cart?.total?.cartServiceCharge &&
            allowServiceChargeRemoval &&
            showRemoveServiceCharge && (
              <ConfirmationModal
                isOpen={showRemoveServiceCharge}
                onClose={toggleServiceChargeModal(false)}
                onConfirm={removeServiceChargeIfRemovable}
                title={intl.formatMessage({
                  defaultMessage: 'Remove service charge?',
                  id: '/5IIca',
                })}
                description={intl.formatMessage({
                  defaultMessage: 'Are you sure you want to remove this?',
                  id: 'beq9m5',
                })}
                cancelLabel={intl.formatMessage({
                  defaultMessage: 'Keep',
                  id: 'pNTyiX',
                })}
                confirmLabel={intl.formatMessage({
                  defaultMessage: 'Remove',
                  id: 'G/yZLu',
                })}
              />
            )}
        </>
      )}
    </>
  )
}
